import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingBag, Plus, ChevronRight, Package, Filter, ExternalLink, Users, Sparkles } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

export default function BrandProfilePage() {
  const { brandName } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [page, setPage] = useState(1);

  const decodedBrand = decodeURIComponent(brandName);

  const fetchBrand = useCallback(async (cat = null, pg = 1) => {
    try {
      let url = `${API}/brand-profile/${encodeURIComponent(decodedBrand)}?page=${pg}&limit=20`;
      if (cat) url += `&category=${cat}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Brand not found');
      const data = await response.json();
      if (pg === 1) {
        setBrand(data);
      } else {
        setBrand(prev => ({ ...prev, products: [...prev.products, ...data.products], page: data.page, has_more: data.has_more }));
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Brand not found');
    } finally {
      setLoading(false);
    }
  }, [decodedBrand]);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  const handleCategoryFilter = (cat) => {
    const newCat = cat === activeCategory ? null : cat;
    setActiveCategory(newCat);
    setPage(1);
    setLoading(true);
    fetchBrand(newCat, 1);
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchBrand(activeCategory, nextPage);
  };

  if (loading && !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!brand) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-lg font-semibold mb-2">Brand not found</p>
          <button onClick={() => navigate(-1)} className="text-primary text-sm">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button data-testid="brand-back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="brand-name-header">{brand.brand}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto">
        {/* Brand Hero */}
        <div className="px-4 py-6 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
              <span className="text-2xl font-black text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {brand.brand.charAt(0)}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="brand-hero-name">{brand.brand}</h2>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Package className="w-3.5 h-3.5" />
                  {brand.total_products} products
                </span>
                {brand.community_checklist_count > 0 && (
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="w-3.5 h-3.5" />
                    {brand.community_checklist_count} checklists
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Brand Checklists Section */}
        {brand.brand_checklists?.length > 0 && (
          <div className="px-4 py-5 border-b border-border">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Curated Routines</h3>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-1">
              {brand.brand_checklists.map(bc => (
                <div key={bc.brand_checklist_id} className="shrink-0 w-56 bg-white rounded-xl border border-border p-3 shadow-sm">
                  <span className="inline-block px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary/10 text-primary mb-2">{bc.skin_type}</span>
                  <p className="text-xs font-bold leading-tight mb-2">{bc.name}</p>
                  <div className="space-y-1.5">
                    {(bc.product_details || []).slice(0, 3).map((p, i) => (
                      <p key={p.product_id} className="text-[10px] text-muted-foreground truncate">
                        {i + 1}. {p.name}
                      </p>
                    ))}
                    {(bc.product_details?.length || 0) > 3 && (
                      <p className="text-[10px] text-primary font-medium">+{bc.product_details.length - 3} more</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Category Filter */}
        {brand.categories?.length > 1 && (
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center gap-2 mb-2">
              <Filter className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs text-muted-foreground font-medium">Filter by category</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {brand.categories.map(cat => (
                <button
                  key={cat}
                  data-testid={`filter-${cat}`}
                  onClick={() => handleCategoryFilter(cat)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    activeCategory === cat
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        <div className="px-4 py-4">
          <p className="text-xs text-muted-foreground mb-3">
            {brand.filtered_count} {activeCategory || ''} products
          </p>
          <div className="space-y-2.5">
            {brand.products.map(product => (
              <div key={product.product_id} className="flex items-center gap-3 p-3 rounded-xl bg-white border border-border" data-testid={`product-${product.product_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground leading-tight">{product.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {product.product_type?.replace('_', ' ')}
                    {product.price ? ` · ${product.price}` : ''}
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  {product.purchase_url && (
                    <a
                      href={product.purchase_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-primary text-white text-[10px] font-semibold hover:bg-primary/90 transition-colors"
                    >
                      <ShoppingBag className="w-3 h-3" />
                      Buy
                    </a>
                  )}
                  <button
                    onClick={() => navigate('/create-checklist', { state: { preselectedProduct: product.product_id } })}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-primary text-primary text-[10px] font-semibold hover:bg-primary/5 transition-colors"
                  >
                    <Plus className="w-3 h-3" />
                    Add
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Load More */}
          {brand.has_more && (
            <button
              data-testid="load-more-products"
              onClick={loadMore}
              className="w-full mt-4 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-gray-50 transition-colors"
            >
              Load more products
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
